import z from 'zod'

export const BatteryContractSchema = z
  .object({
    contractStartDate: z.coerce.date().describe('Vertragsbeginn (Datum des Vertragsabschlusses)'),
    contractEndDate: z.coerce
      .date()
      .describe(
        'Vertragsende (Vertragsende zum Zeitpunkt des Vertragsabschlusses zu ermitteln aus: geplanter Vermarktungsbeginn + Vertragsdauer in Jahren; nach tatsächlichem Vermarktungsbeginn ist das Vertragsende ggf. anzupassen)'
      ),
    internalContactPerson1: z.string().trim().min(1).describe('Interner Ansprechpartner 1'),
    internalContactPerson2: z.string().optional().describe('Interner Ansprechpartner 2'),
    productGroup: z
      .enum([
        'Stand-alone Batterie',
        'Co-Location Grünstrombatterie',
        'Co-Location Graustrombatterie',
        'Co-Location Mischstrombatterie',
      ])
      .describe('Produktgruppe'),
    markets: z.enum(['PRL', 'ID, DA', 'PRL, SRL, DA, ID']).describe('Nutzbare Märkte'),
    contractStatus: z
      .enum([
        'Aktiver Vertrag',
        'Angebot versendet',
        'Vertrag gewonnen',
        'Gekündigt durch EnBW',
        'Gekündigt durch Kunde',
        'Beendeter Vertrag',
        'Vertrag beendet (Kündigung)',
        'Vertrag beendet (Aufhebung, Änderung)',
      ])
      .default('Vertrag gewonnen')
      .describe('Vertragsstatus'),
    beginOfMarketingDatePlanned: z.coerce.date().describe('Geplanter Vermarktungsbeginn'),
    beginOfMarketingDate: z.coerce.date().optional().describe('Tatsächlicher Vermarktungsbeginn'),
    extensionPeriod: z
      .enum(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'])
      .describe('Dauer der Vertragsverlängerung in Jahren'),
    nextContractProlongationDate: z.coerce.date().describe('Ablaufdatum erstmalige Kündigungsfrist'),
    noticePeriod: z.number().int().min(1).max(12).describe('Kündigungsfrist (Monate)'),
  })
  .describe('Vertragsdaten')
  .check((ctx) => {
    const {
      contractStartDate,
      contractEndDate,
      beginOfMarketingDatePlanned,
      beginOfMarketingDate,
      nextContractProlongationDate,
    } = ctx.value
    const today = toCalendarDay(new Date())

    type Field = keyof typeof ctx.value

    const addIssue = (path: Field, input: Date, message: string) =>
      ctx.issues.push({ code: 'custom', input, path: [path], message })

    const requireNotInPast = (field: Field, value: Date | undefined, message: string) => {
      if (value && toCalendarDay(value) < today) addIssue(field, value, message)
    }

    const requireAfterContractStart = (field: Field, value: Date | undefined, message: string, strict = false) => {
      if (contractStartDate && value) {
        const cmp = toCalendarDay(value) - toCalendarDay(contractStartDate)
        if (strict ? cmp <= 0 : cmp < 0) addIssue(field, value, message)
      }
    }

    requireNotInPast('contractEndDate', contractEndDate, 'Vertragsende darf nicht in der Vergangenheit liegen')

    requireAfterContractStart('contractEndDate', contractEndDate, 'Vertragsende muss nach Vertragsbeginn liegen', true)

    requireAfterContractStart(
      'beginOfMarketingDatePlanned',
      beginOfMarketingDatePlanned,
      'Geplanter Vermarktungsbeginn muss größer oder gleich Vertragsbeginn sein'
    )
    requireAfterContractStart(
      'nextContractProlongationDate',
      nextContractProlongationDate,
      'Nächstes Verlängerungsdatum muss größer oder gleich Vertragsbeginn sein'
    )
    requireAfterContractStart(
      'beginOfMarketingDate',
      beginOfMarketingDate,
      'Vermarktungsbeginn muss größer oder gleich Vertragsbeginn sein'
    )
  })

// Compare by UTC calendar day. Runs in Lambda (UTC) and in the browser (user TZ); using
// local getters would flip the day across midnight between environments for the same stored
// ISO string, producing false positives on cross-field date checks.
function toCalendarDay(value: Date | string): number {
  const d = new Date(value)
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate()
}

export const BatteryContractSchemaForSharepoint = BatteryContractSchema.safeExtend({
  contractNumber: z.string().min(1),
  dealId: z.string().min(1),
  contractValidFrom: z.coerce.date(),
  contractValidUntil: z.coerce.date(),
})
